import { useCallback, useReducer } from 'react';

// Feature selection state.
const useFeatures = (
    idField: string,
    nameField: string,
    maxFeatures?: number
): any[] => {
    // Used by the point tool to add/remove from current selection.
    const difference = (currentFeatures: any, newFeatures: any): any => {
        const features = [...currentFeatures, ...newFeatures];
        const featureIds = features.map((f: any) => f.attributes[idField]);
        const uniqueIds = featureIds.filter(
            (id: string) =>
                featureIds.lastIndexOf(id) === featureIds.indexOf(id)
        );
        return features.filter(
            (f: any) => uniqueIds.indexOf(f.attributes[idField]) !== -1
        );
    };

    // Add.
    const add = (currentFeatures: any, newFeatures: any): any => {
        const features = [...currentFeatures, ...newFeatures];
        const featureIds = features.map((f: any) => f.attributes[idField]);
        var i = featureIds.length;
        while (i--) {
            const id = featureIds[i];
            if (featureIds.lastIndexOf(id) !== featureIds.indexOf(id)) {
                featureIds.splice(i, 1);
                features.splice(i, 1);
            }
        }
        return features;
    };

    // Sort.
    const sortByName = (a: any, b: any) => {
        if (a.attributes[nameField] < b.attributes[nameField]) return -1;
        if (a.attributes[nameField] > b.attributes[nameField]) return 1;
        return 0;
    };

    // The reducer stores the selection.
    const featureReducer = (state: any, action: any) => {
        let arr = [];
        switch (action.type) {
            case 'clear':
                // Clear the selection.
                return [];
            case 'add':
                // Add features.
                arr = add(state, action.features).sort(sortByName);
                break;
            case 'remove':
                // Remove a feature by passing an objectId.
                arr = state.filter(
                    (f: any) => f.attributes[idField] !== action.id
                );
                break;
            case 'difference':
                // Add features not in the current selection.
                // Remove features that are in the current selection
                arr = difference(state, action.features);
                break;
            case 'set':
                // Replace entire selection.
                arr = [...action.features];
                break;
            default:
                throw new Error();
        }
        let arrSort = arr.sort(sortByName);
        if (maxFeatures !== undefined) return arrSort.slice(0, maxFeatures);
        else return arrSort;
    };

    const [selection, setSelection] = useReducer(featureReducer, []);

    // Action Creators.
    const setFeatures = useCallback(
        (features: any[]) => {
            setSelection({ type: 'set', features });
        },
        [setSelection]
    );

    const addFeatures = useCallback(
        (features: any[]) => {
            setSelection({ type: 'add', features });
        },
        [setSelection]
    );

    const clearFeatures = useCallback(() => {
        setSelection({ type: 'clear' });
    }, [setSelection]);

    const removeFeature = useCallback(
        (id: string) => {
            setSelection({ type: 'remove', id });
        },
        [setSelection]
    );

    const differenceFeatures = useCallback(
        (features: any[]) => {
            setSelection({ type: 'difference', features });
        },
        [setSelection]
    );

    const ids = selection.map((f: any) => f.attributes[idField]);
    const names = selection.map((f: any) => f.attributes[nameField]);

    return [
        selection,
        ids,
        names,
        setFeatures,
        addFeatures,
        clearFeatures,
        removeFeature,
        differenceFeatures
    ];
};
export default useFeatures;
